// Conversion helpers for the `request_review` remote tool. The astral2 agent
// emits ONE tool whose shape depends on the disposition (see astral.k8s.yaml,
// REVIEW-FACT SHAPES):
//   - verify_extraction (document/IDP review): `source` + `extracted_fields`
//     + `missing_fields` — surfaced as the existing IDP "Provide missing values"
//     dialog so the rain flow matches the non-rain astral flow.
//   - verify_checkpoint (approval gate): an approve/reject decision, optionally
//     accompanied by `checkpoint_facts` (runtime values the user reviews before
//     deciding). With facts it opens a dedicated approval dialog; without any
//     facts it's a plain approve/reject rendered inline in the chat.
// These helpers turn the decoded tool args into the data those surfaces consume,
// and turn the user's decision/edits back into a tool_result value.

import type { Rainv1Value } from '@/generated/hooks/rain/models';
import type {
  IIDPBoundingBox,
  IIDPFact,
  IIDPFactInputData,
} from '@/modules/idp/types';

import type { TRainValueJs } from '../utils/value';

/** The two `request_review` dispositions, by the args the tool carries. */
export type TReviewMode = 'extraction' | 'checkpoint' | 'unknown';

const isRecord = (v: TRainValueJs): v is { [key: string]: TRainValueJs } =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const asString = (v: TRainValueJs): string | undefined =>
  typeof v === 'string' ? v : undefined;

const asNumber = (v: TRainValueJs): number | undefined => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) {
    return Number(v);
  }
  return undefined;
};

const asBoundingBox = (v: TRainValueJs): IIDPBoundingBox | undefined => {
  if (!isRecord(v)) return undefined;
  const x = asNumber(v.x);
  const y = asNumber(v.y);
  const width = asNumber(v.width);
  const height = asNumber(v.height);
  if (
    x === undefined ||
    y === undefined ||
    width === undefined ||
    height === undefined
  ) {
    return undefined;
  }
  return { x, y, width, height };
};

const nonEmptyArray = (v: TRainValueJs): boolean =>
  Array.isArray(v) && v.length > 0;

/**
 * Classifies a `request_review` call by which fact group it actually carries.
 *
 * The agent sends ALL the group keys and leaves the unused ones as EMPTY lists,
 * so presence of a key is not enough — e.g. an extraction review arrives with
 * `missing_fields` populated but `checkpoint_facts: []`. We therefore key off
 * non-empty content:
 *   - a non-empty `checkpoint_facts` is a checkpoint (approval over runtime
 *     values);
 *   - otherwise a source or any extracted/missing field selects the IDP review;
 *   - otherwise a call carrying only a `message` is a plain approve/reject
 *     checkpoint with nothing to show (the inline buttons case).
 */
export function detectReviewMode(
  args: Record<string, TRainValueJs>,
): TReviewMode {
  if (nonEmptyArray(args.checkpoint_facts)) return 'checkpoint';
  const hasSource = (asString(args.source) ?? '').trim().length > 0;
  if (
    hasSource ||
    nonEmptyArray(args.extracted_fields) ||
    nonEmptyArray(args.missing_fields)
  ) {
    return 'extraction';
  }
  // No fact group of any kind: a `request_review` that carries only a message is
  // a bare approval gate (verify_checkpoint with no runtime facts to review).
  const hasMessage = (asString(args.message) ?? '').trim().length > 0;
  if (hasMessage) return 'checkpoint';
  return 'unknown';
}

/**
 * Builds the `IIDPFactInputData` the shared IDP dialog consumes from a
 * verify_extraction `request_review` call. Found fields become `present` facts
 * (carrying their extraction metadata verbatim); missing field names become
 * `missing` facts the user fills in. Context is `need_information` so the dialog
 * renders the editable "Provide missing values" experience.
 */
export function buildReviewIdpData(
  args: Record<string, TRainValueJs>,
): IIDPFactInputData {
  const source = asString(args.source);
  const extracted = Array.isArray(args.extracted_fields)
    ? args.extracted_fields
    : [];
  const missing = Array.isArray(args.missing_fields) ? args.missing_fields : [];

  const facts: IIDPFact[] = [];

  for (const entry of extracted) {
    if (!isRecord(entry)) continue;
    const field = asString(entry.field);
    if (!field) continue;
    // astral2/rain reports `page_number` 0-indexed, but the shared IDP pipeline
    // (`convertToLocation`) treats it as 1-indexed (subtracts 1). Shift here so a
    // page-0 box lands on the first page instead of page_index -1. Bounding boxes
    // ride through verbatim as ABSOLUTE PIXELS — the page renderer normalizes
    // them against the native page size (see normalizePixelSpaceFields).
    const rawPage = asNumber(entry.page_number);
    facts.push({
      field,
      type: asString(entry.type) ?? 'text',
      status: 'present',
      value: entry.value != null ? String(entry.value) : undefined,
      confidence: asNumber(entry.confidence),
      page_number: rawPage != null ? rawPage + 1 : undefined,
      bounding_box: asBoundingBox(entry.bounding_box),
    });
  }

  for (const name of missing) {
    const field = asString(name);
    if (!field) continue;
    facts.push({ field, type: 'text', status: 'missing' });
  }

  return {
    context: 'need_information',
    source,
    factGroups: [{ source: source ? { name: source } : undefined, facts }],
  };
}

/** Total fields surfaced for review (found + missing) — drives the card count. */
export function countReviewFields(args: Record<string, TRainValueJs>): number {
  const extracted = Array.isArray(args.extracted_fields)
    ? args.extracted_fields.length
    : 0;
  const missing = Array.isArray(args.missing_fields)
    ? args.missing_fields.length
    : 0;
  return extracted + missing;
}

/** The verbatim wire values the agent expects back for a checkpoint decision. */
export type TCheckpointDecision = 'approved' | 'rejected';

/** A single runtime value a verify_checkpoint asks the user to review. */
export interface ICheckpointFact {
  /** Human label for the value; the agent may omit it for value-only facts. */
  field?: string;
  /** The runtime value to display, stringified. */
  value: string;
  /** Render hint carried by the agent (e.g. 'text'); defaults to 'text'. */
  type: string;
}

/**
 * Decodes the `checkpoint_facts` arg into the read-only rows the approval dialog
 * shows. Malformed entries (non-objects / missing value) are dropped. The chosen
 * approve/reject decision is sent separately via {@link buildReviewResultValue},
 * so this carries no decision field.
 */
export function parseCheckpointFacts(
  args: Record<string, TRainValueJs>,
): ICheckpointFact[] {
  const raw = Array.isArray(args.checkpoint_facts) ? args.checkpoint_facts : [];
  const facts: ICheckpointFact[] = [];
  for (const entry of raw) {
    if (!isRecord(entry)) continue;
    const value = entry.value != null ? String(entry.value) : undefined;
    if (value === undefined) continue;
    facts.push({
      field: asString(entry.field),
      value,
      type: asString(entry.type) ?? 'text',
    });
  }
  return facts;
}

/**
 * Encodes the user's reviewed values as the remote tool's result `content`.
 * The dialog hands back the final value per field (filled missing + approved /
 * corrected extracted); the agent treats these as the source of truth for the
 * reviewed fields on its next cycle.
 *
 * NOTE: the tool's `output` is typed `Any` with no formal response schema in
 * the astral config, so this structured shape is our contract — keep it the
 * single place that defines it.
 */
export function buildReviewResultValue(
  editedValues: Record<string, string>,
): Rainv1Value {
  const facts: Rainv1Value[] = Object.entries(editedValues).map(
    ([field, value]) => ({
      object_value: {
        fields: {
          field: { string_value: field },
          value: { string_value: value },
        },
      },
    }),
  );
  return {
    object_value: {
      fields: {
        facts: { list_value: { values: facts } },
      },
    },
  };
}
